/**
 * OGC & External Service Clients
 * WMS, WFS, WMTS, XYZ, ArcGIS, OpenStreetMap Overpass
 */

/** Extract the text of an OWS/WMS exception from a capabilities response, if any. */
export function owsExceptionText(xml) {
  const node = xml.querySelector('ExceptionText, ServiceException');
  const t = node?.textContent?.trim();
  return t || null;
}

/**
 * Parse WMS GetCapabilities and return available layers.
 * @param {string} baseUrl - WMS service URL
 * @returns {Promise<Array<{name, title, abstract, bbox}>>}
 */
export async function getWMSLayers(baseUrl) {
  const url = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}service=WMS&request=GetCapabilities`;
  const res = await fetch(url);
  const text = await res.text();
  const parser = new DOMParser();
  const xml = parser.parseFromString(text, 'text/xml');
  const exc = owsExceptionText(xml);
  if (exc) throw new Error(`WMS server error: ${exc}`);
  const layerNodes = xml.querySelectorAll('Layer > Layer');
  const layers = [];
  layerNodes.forEach(node => {
    const name = node.querySelector('Name')?.textContent;
    const title = node.querySelector('Title')?.textContent;
    const abstract = node.querySelector('Abstract')?.textContent;
    if (name) {
      layers.push({ name, title: title || name, abstract: abstract || '' });
    }
  });
  return layers;
}

/**
 * Build a WMS tile URL for MapLibre raster source.
 * @param {string} baseUrl - WMS service URL
 * @param {string} layerName - layer name from GetCapabilities
 * @param {object} options
 * @returns {string} tile URL template
 */
export function buildWMSTileUrl(baseUrl, layerName, options = {}) {
  const { format = 'image/png', transparent = true, srs = 'EPSG:3857' } = options;
  const sep = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${sep}service=WMS&version=1.1.1&request=GetMap&layers=${encodeURIComponent(layerName)}&styles=&srs=${srs}&format=${encodeURIComponent(format)}&transparent=${transparent}&width=256&height=256&bbox={bbox-epsg-3857}`;
}

/**
 * Parse WFS GetCapabilities and return available feature types.
 * @param {string} baseUrl - WFS service URL
 * @returns {Promise<Array<{name, title}>>}
 */
export async function getWFSFeatureTypes(baseUrl) {
  const url = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}service=WFS&request=GetCapabilities`;
  const res = await fetch(url);
  const text = await res.text();
  const parser = new DOMParser();
  const xml = parser.parseFromString(text, 'text/xml');
  const exc = owsExceptionText(xml);
  if (exc) throw new Error(`WFS server error: ${exc}`);
  const types = [];
  xml.querySelectorAll('FeatureType').forEach(node => {
    const name = node.querySelector('Name')?.textContent;
    const title = node.querySelector('Title')?.textContent;
    if (name) types.push({ name, title: title || name });
  });
  return types;
}

/**
 * Fetch WFS features as GeoJSON.
 * @param {string} baseUrl - WFS service URL
 * @param {string} typeName - feature type name
 * @param {object} options
 * @returns {Promise<object>} GeoJSON FeatureCollection
 */
export async function fetchWFSFeatures(baseUrl, typeName, options = {}) {
  const { maxFeatures = 5000, srs = 'EPSG:4326' } = options;
  const sep = baseUrl.includes('?') ? '&' : '?';
  const url = `${baseUrl}${sep}service=WFS&version=2.0.0&request=GetFeature&typeName=${encodeURIComponent(typeName)}&outputFormat=application/json&srsName=${srs}&count=${maxFeatures}`;
  const res = await fetch(url);
  return res.json();
}

/**
 * Parse WMTS GetCapabilities and return available layers with usable tile URL templates.
 * Prefers RESTful ResourceURL templates; falls back to KVP GetTile.
 * @param {string} baseUrl
 * @returns {Promise<Array<{identifier, title, tileMatrixSet, format, tileUrl}>>}
 */
export async function getWMTSLayers(baseUrl) {
  const url = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}service=WMTS&request=GetCapabilities`;
  const res = await fetch(url);
  const text = await res.text();
  const parser = new DOMParser();
  const xml = parser.parseFromString(text, 'text/xml');
  const exc = owsExceptionText(xml);
  if (exc) throw new Error(`WMTS server error: ${exc}`);
  const layers = [];

  // Layer nodes live under Contents; skip TileMatrixSet definitions
  const contents = xml.querySelector('Contents') || xml;
  contents.childNodes.forEach(node => {
    if (node.nodeName !== 'Layer') return;

    const identifier = node.querySelector(':scope > Identifier')?.textContent
      || node.getElementsByTagName('ows:Identifier')[0]?.textContent;
    if (!identifier) return;
    const title = node.querySelector(':scope > Title')?.textContent
      || node.getElementsByTagName('ows:Title')[0]?.textContent
      || identifier;
    const format = node.querySelector('Format')?.textContent || 'image/png';

    // Default style
    let style = 'default';
    node.querySelectorAll('Style').forEach(s => {
      const id = s.querySelector('Identifier')?.textContent || s.getElementsByTagName('ows:Identifier')[0]?.textContent;
      if (s.getAttribute('isDefault') === 'true' && id) style = id;
      else if (style === 'default' && id) style = id;
    });

    // Pick a Web-Mercator-compatible TileMatrixSet if available
    const matrixSets = [...node.querySelectorAll('TileMatrixSetLink > TileMatrixSet')].map(n => n.textContent);
    const tileMatrixSet = matrixSets.find(s => /3857|GoogleMapsCompatible|WebMercator/i.test(s)) || matrixSets[0];
    if (!tileMatrixSet) return;

    // RESTful template if provided
    const resourceNode = [...node.querySelectorAll('ResourceURL')].find(r => r.getAttribute('resourceType') === 'tile');
    let tileUrl;
    if (resourceNode?.getAttribute('template')) {
      tileUrl = resourceNode.getAttribute('template')
        .replace('{Style}', style)
        .replace('{TileMatrixSet}', tileMatrixSet)
        .replace('{TileMatrix}', '{z}')
        .replace('{TileRow}', '{y}')
        .replace('{TileCol}', '{x}');
    } else {
      // KVP fallback
      const clean = baseUrl.split('?')[0];
      tileUrl = `${clean}?service=WMTS&request=GetTile&version=1.0.0&layer=${encodeURIComponent(identifier)}&style=${encodeURIComponent(style)}&tilematrixset=${encodeURIComponent(tileMatrixSet)}&TileMatrix={z}&TileRow={y}&TileCol={x}&format=${encodeURIComponent(format)}`;
    }

    layers.push({ identifier, title, tileMatrixSet, format, tileUrl });
  });
  return layers;
}

/**
 * Build an ArcGIS REST tile URL for MapLibre.
 * @param {string} baseUrl - ArcGIS MapServer or FeatureServer URL
 * @returns {string} tile URL template
 */
export function buildArcGISTileUrl(baseUrl) {
  const clean = baseUrl.replace(/\/+$/, '');
  return `${clean}/tile/{z}/{y}/{x}`;
}

/**
 * Fetch ArcGIS FeatureServer features as GeoJSON.
 * @param {string} baseUrl - FeatureServer layer URL (e.g. .../FeatureServer/0)
 * @param {object} options
 * @returns {Promise<object>} GeoJSON FeatureCollection
 */
export async function fetchArcGISFeatures(baseUrl, options = {}) {
  const { where = '1=1', maxRecords = 5000 } = options;
  const clean = baseUrl.replace(/\/+$/, '');
  const url = `${clean}/query?where=${encodeURIComponent(where)}&outFields=*&returnGeometry=true&f=geojson&resultRecordCount=${maxRecords}`;
  const res = await fetch(url);
  return res.json();
}

/**
 * Replace Overpass-Turbo's `{{bbox}}` macro with a raw-API bbox (south,west,north,east).
 * @param {string} query
 * @param {number[]|null} bbox - [west, south, east, north]
 */
export function substituteOverpassBbox(query, bbox) {
  if (!/\{\{\s*bbox\s*\}\}/i.test(query)) return query;
  if (!bbox || bbox.length < 4 || bbox.some(v => !Number.isFinite(v))) {
    throw new Error('The query uses {{bbox}} but the map extent is not available yet; pan the map and retry.');
  }
  const [w, s, e, n] = bbox;
  return query.replace(/\{\{\s*bbox\s*\}\}/gi, `${s.toFixed(6)},${w.toFixed(6)},${n.toFixed(6)},${e.toFixed(6)}`);
}

/**
 * Query OpenStreetMap Overpass API.
 * @param {string} query - Overpass QL query string; `{{bbox}}` is replaced by `bbox`
 * @param {number[]|null} [bbox] - [west, south, east, north] of the current map view
 * @returns {Promise<object>} GeoJSON FeatureCollection
 */
export async function queryOverpass(query, bbox = null) {
  const q = substituteOverpassBbox(query, bbox);
  const res = await fetch('https://overpass-api.de/api/interpreter', {
    method: 'POST',
    body: `data=${encodeURIComponent(`[out:json][timeout:30];${q}out body;>;out skel qt;`)}`,
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
  if (!res.ok) {
    const text = (await res.text().catch(() => '')).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    throw new Error(`Overpass error ${res.status}${text ? `: ${text.slice(0, 300)}` : ''}`);
  }
  const data = await res.json();
  // Convert Overpass JSON to GeoJSON
  return overpassToGeoJSON(data);
}

/**
 * Convert Overpass JSON to GeoJSON FeatureCollection.
 */
function overpassToGeoJSON(data) {
  const nodes = {};
  const features = [];

  // Index nodes
  (data.elements || []).forEach(el => {
    if (el.type === 'node') {
      nodes[el.id] = [el.lon, el.lat];
      if (el.tags && Object.keys(el.tags).length > 0) {
        features.push({
          type: 'Feature',
          properties: { ...el.tags, osm_id: el.id, osm_type: 'node' },
          geometry: { type: 'Point', coordinates: [el.lon, el.lat] },
        });
      }
    }
  });

  // Ways → LineStrings or Polygons
  (data.elements || []).forEach(el => {
    if (el.type === 'way' && el.nodes) {
      const coords = el.nodes.map(id => nodes[id]).filter(Boolean);
      if (coords.length < 2) return;
      const isClosed = el.nodes[0] === el.nodes[el.nodes.length - 1] && coords.length >= 4;
      features.push({
        type: 'Feature',
        properties: { ...el.tags, osm_id: el.id, osm_type: 'way' },
        geometry: isClosed
          ? { type: 'Polygon', coordinates: [coords] }
          : { type: 'LineString', coordinates: coords },
      });
    }
  });

  return { type: 'FeatureCollection', features };
}

/**
 * Validate a URL string.
 */
export function isValidUrl(str) {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
}

/**
 * Service type definitions for the UI.
 */
export const SERVICE_TYPES = [
  { id: 'xyz', label: 'XYZ Tiles', icon: '🗺️', desc: 'Slippy-map tile URL' },
  { id: 'wms', label: 'WMS', icon: '🌐', desc: 'OGC Web Map Service' },
  { id: 'wfs', label: 'WFS', icon: '📊', desc: 'OGC Web Feature Service' },
  { id: 'wmts', label: 'WMTS', icon: '🔲', desc: 'OGC Web Map Tile Service' },
  { id: 'osm', label: 'OpenStreetMap', icon: '🟢', desc: 'Query Overpass API' },
  { id: 'arcgis', label: 'ArcGIS', icon: '🟠', desc: 'FeatureServer / MapServer' },
];
